import { createSlice, createSelector } from '@reduxjs/toolkit';
import { fetchAllDieselPrices, fetchStateDieselPrices, fetchCityDieselPrice } from './dieselPriceApi';

const initialState = {
    all: {
        loading: false,
        error: null,
        data: null, // { scraped_at, data_source, states: { State: { City: { price, change } } } }
        fetchedAt: null,
    },
    byState: {}, // key: resolved state -> { loading, error, data }
    byCity: {}, // key: `${state}::${city}` -> { loading, error, data }
};

const normalizeKey = (value) => String(value || '').trim();

const dieselPriceSlice = createSlice({
    name: 'dieselPrice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // All
            .addCase(fetchAllDieselPrices.pending, (state) => {
                state.all.loading = true;
                state.all.error = null;
            })
            .addCase(fetchAllDieselPrices.fulfilled, (state, action) => {
                state.all.loading = false;
                state.all.data = action.payload;
                state.all.fetchedAt = Date.now();
            })
            .addCase(fetchAllDieselPrices.rejected, (state, action) => {
                state.all.loading = false;
                state.all.error = action.payload?.error || 'Failed to load';
            })
            // State
            .addCase(fetchStateDieselPrices.pending, (state, action) => {
                const key = normalizeKey(action.meta?.arg);
                if (!state.byState[key]) state.byState[key] = { loading: false, error: null, data: null };
                state.byState[key].loading = true;
                state.byState[key].error = null;
            })
            .addCase(fetchStateDieselPrices.fulfilled, (state, action) => {
                const key = normalizeKey(action.meta?.arg);
                if (!state.byState[key]) state.byState[key] = { loading: false, error: null, data: null };
                state.byState[key].loading = false;
                state.byState[key].data = action.payload;
            })
            .addCase(fetchStateDieselPrices.rejected, (state, action) => {
                const key = normalizeKey(action.meta?.arg);
                if (!state.byState[key]) state.byState[key] = { loading: false, error: null, data: null };
                state.byState[key].loading = false;
                state.byState[key].error = action.payload?.error || 'Failed to load';
            })
            // City
            .addCase(fetchCityDieselPrice.pending, (state, action) => {
                const { state: s, city } = action.meta?.arg || {};
                const key = `${normalizeKey(s)}::${normalizeKey(city)}`;
                if (!state.byCity[key]) state.byCity[key] = { loading: false, error: null, data: null };
                state.byCity[key].loading = true;
                state.byCity[key].error = null;
            })
            .addCase(fetchCityDieselPrice.fulfilled, (state, action) => {
                const { state: s, city } = action.meta?.arg || {};
                const key = `${normalizeKey(s)}::${normalizeKey(city)}`;
                if (!state.byCity[key]) state.byCity[key] = { loading: false, error: null, data: null };
                state.byCity[key].loading = false;
                state.byCity[key].data = action.payload;
            })
            .addCase(fetchCityDieselPrice.rejected, (state, action) => {
                const { state: s, city } = action.meta?.arg || {};
                const key = `${normalizeKey(s)}::${normalizeKey(city)}`;
                if (!state.byCity[key]) state.byCity[key] = { loading: false, error: null, data: null };
                state.byCity[key].loading = false;
                state.byCity[key].error = action.payload?.error || 'Failed to load';
            });
    },
});

export const selectDieselPriceAll = (state) => state?.dieselPrice?.all || {};
export const selectDieselPriceAllData = createSelector(
    selectDieselPriceAll,
    (all) => all?.data || null
);
export const selectDieselPriceStates = createSelector(
    selectDieselPriceAllData,
    (data) => data?.states || {}
);
export const selectDieselPriceByState = (state, stateName) => {
    const key = normalizeKey(stateName);
    return state?.dieselPrice?.byState?.[key] || {};
};
export const selectDieselPriceByCity = (state, stateName, cityName) => {
    const key = `${normalizeKey(stateName)}::${normalizeKey(cityName)}`;
    return state?.dieselPrice?.byCity?.[key] || {};
};

export default dieselPriceSlice.reducer;
export { dieselPriceSlice };
